import React from "react";

// ---------------------------------------------------------------------------
// Journey Storybooks
// Split into two honest layers:
//   1. Published Journey Storybooks — an evidence-backed or illustrative
//      pattern with an explicit status label (Simulation / Live / etc.).
//   2. Candidate sector patterns — patterns being prepared. These are
//      generic sector descriptions, not published Storybooks.
// This protects credibility around honest demonstrator status.
// ---------------------------------------------------------------------------

const PUBLISHED = [
  {
    id: "authority-journey",
    number: "01",
    label: "Authority Journey",
    status: "Simulation",
    challenge:
      "Illustrative border-and-freight release decision that stalls when several parties have partial information but no explicit authority to move it forward.",
    roles:
      "Operator, customs role, resilience officer, sponsor. Consortium partners and specialists on demand.",
    protocol:
      "Bounded request → evidence stays with each custodian → authorised release, refusal or escalation → receipt of what was shared, withheld and decided.",
    outcome:
      "A reusable release-and-handoff pattern for cross-organisation freight, materials and compliance evidence.",
    href: "https://authority-journey.katlastechnology.com/",
    cta: "Open Storybook",
  },
];

const CANDIDATE = [
  {
    id: "supply-industrial",
    label: "Supply chain and industrial handoffs",
    challenge:
      "A supplier exception needs an authorised release before goods, paperwork or compliance evidence can move on to the next party.",
    roles:
      "Supplier, logistics, quality, buyer, customs role, resilience officer.",
    protocol:
      "Bounded request → evidence remains with custodian → authorised release, refusal or escalation → signed receipt for the next handoff.",
    outcome:
      "A reusable release-and-handoff pattern for any critical component, material or shipment class.",
  },
  {
    id: "health-care",
    label: "Health and care coordination",
    challenge:
      "A handoff between primary care, a specialist and a family member where consent, evidence and next-action authority must travel together.",
    roles:
      "Primary clinician, specialist, patient, family carer, administrative role.",
    protocol:
      "Bounded request → clinical evidence stays with its custodian → authorised decision by the accountable role → receipt of what was shared, withheld and decided.",
    outcome:
      "A reusable consent-and-decision pattern across pathways where care crosses organisations.",
  },
  {
    id: "public-service",
    label: "Public-service and resilience decisions",
    challenge:
      "An incident where several agencies see the same signal but only one role is authorised to commit, refuse or escalate the response.",
    roles:
      "Duty officer, sector operator, regulator, resilience lead, incident controller.",
    protocol:
      "Signal request → evidence held by each agency remains local → authorised commit or escalation → receipt showing who acted under which mandate.",
    outcome:
      "A reusable multi-agency exception pattern for infrastructure, safety and resilience events.",
  },
  {
    id: "research-funding",
    label: "Research, funding and innovation ecosystems",
    challenge:
      "A funded collaboration where partners coordinate evidence, custody and approval without merging their data into one platform.",
    roles:
      "Consortium lead, technical partner, evaluator, funder, sector assessor.",
    protocol:
      "Bounded evidence request → partner data stays at source → authorised evaluation or claim decision → receipt of what evidence supported the outcome.",
    outcome:
      "A reusable governed-consortium pattern for calls, challenges and innovation ecosystems.",
  },
];

const rowFields = (story) => [
  { k: "Challenge", v: story.challenge },
  { k: "Roles", v: story.roles },
  { k: "Protocol", v: story.protocol },
  { k: "Reusable outcome", v: story.outcome, accent: true },
];

const PublishedCard = ({ story }) => (
  <article
    data-testid={`storybook-published-${story.id}`}
    className="bg-[#020B14] border border-[#0066FF]/30 p-5 lg:p-6 flex flex-col"
    style={{
      backgroundImage:
        "radial-gradient(ellipse 70% 40% at 20% 0%, rgba(0,102,255,0.10), transparent 60%)",
    }}
  >
    <div className="flex items-baseline gap-3">
      <span className="font-display text-3xl leading-none text-[#0066FF]">
        {story.number}
      </span>
      <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        Storybook
      </span>
      <span className="ml-auto inline-flex items-center gap-2 border border-[#EAB308]/30 px-2 py-1">
        <span className="w-1.5 h-1.5 bg-[#EAB308] inline-block" />
        <span className="mono text-[10px] uppercase tracking-[0.22em] text-[#EAB308]">
          Status: {story.status}
        </span>
      </span>
    </div>

    <h3 className="mt-4 font-display text-xl sm:text-2xl text-white leading-snug">
      {story.label}
    </h3>

    <dl className="mt-5 grid gap-3">
      {rowFields(story).map((row) => (
        <div key={row.k} className="grid grid-cols-[110px,1fr] gap-3 items-start">
          <dt className="mono text-[10px] uppercase tracking-[0.2em] text-[#0066FF] pt-1">
            {row.k}
          </dt>
          <dd
            className={`text-sm leading-relaxed ${
              row.accent ? "text-[#EAB308]" : "text-slate-200"
            }`}
          >
            {row.v}
          </dd>
        </div>
      ))}
    </dl>

    <div className="mt-6 pt-5 border-t border-white/[0.06] flex justify-end">
      <a
        href={story.href}
        data-testid={`storybook-published-${story.id}-cta`}
        className="cta-primary inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white px-4 py-2.5 text-[12px] font-medium rounded-sm"
      >
        {story.cta}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M7 17 17 7M8 7h9v9" />
        </svg>
      </a>
    </div>
  </article>
);

const CandidateCard = ({ story }) => (
  <article
    data-testid={`storybook-candidate-${story.id}`}
    className="bg-[#020B14] border border-white/[0.08] p-5 lg:p-6 flex flex-col"
  >
    <h3 className="font-display text-lg sm:text-xl text-slate-200 leading-snug">
      {story.label}
    </h3>

    <dl className="mt-5 grid gap-3">
      {rowFields(story).map((row) => (
        <div key={row.k} className="grid grid-cols-[110px,1fr] gap-3 items-start">
          <dt className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 pt-1">
            {row.k}
          </dt>
          <dd
            className={`text-sm leading-relaxed ${
              row.accent ? "text-slate-300" : "text-slate-400"
            }`}
          >
            {row.v}
          </dd>
        </div>
      ))}
    </dl>
  </article>
);

export const JourneyStorybooks = () => {
  return (
    <section
      id="journey-storybooks"
      data-testid="section-journey-storybooks"
      className="relative py-20 lg:py-28 border-t border-white/[0.06] bg-[#040F1B]"
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-10 lg:mb-12">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#0066FF]">
            Journey Storybooks
          </div>
          <h2
            data-testid="storybooks-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.06]"
          >
            Reusable patterns{" "}
            <span className="text-slate-400">
              for decisions that cross organisational boundaries.
            </span>
          </h2>
          <p
            data-testid="storybooks-intro"
            className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed"
          >
            Storybooks capture a tested or illustrative governed pattern with
            an honest status label. Each shows the challenge, relevant roles,
            evidence boundary, authority route, protocol and reusable outcome.
          </p>
        </div>

        {/* Layer 1: Published Journey Storybooks */}
        <div className="mb-14 lg:mb-16">
          <div className="flex items-baseline gap-3 mb-5">
            <span className="mono text-[10px] uppercase tracking-[0.22em] text-[#0066FF]">
              Published Journey Storybooks
            </span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
            {PUBLISHED.map((s) => (
              <PublishedCard key={s.id} story={s} />
            ))}
          </div>
        </div>

        {/* Layer 2: Candidate sector patterns */}
        <div>
          <div className="flex items-baseline gap-3 mb-5">
            <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              Candidate sector patterns
            </span>
            <span className="h-px flex-1 bg-white/[0.06]" />
            <span className="mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              Being prepared
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5">
            {CANDIDATE.map((s) => (
              <CandidateCard key={s.id} story={s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
